import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import GroupTable from "@/components/tables/GroupTable";
import TableHead from "@/components/tables/TableHead";
import { fetchGroups } from "@/services/group.service";
import { BUTTONS } from "@/utils/constants/appConstants/commonConstants";
import { ALL_GROUPS } from "@/utils/constants/constants";
import { storeInLocalStorage } from "@/utils/functions/helperFunctions";
import { isFeaturePermitted } from "@/utils/functions/rbac";
import { useDataQueries } from "@/utils/hooks/useDataQueries";
import { getListCacheBootstrap, isListFullyFetched, writeListCache } from "@/utils/listPageCache";

const GroupList = () => {
    const [isCreateGroupPermitted, setIsCreateGroupPermission] = useState(false);
    const [isDeleteGroupPermitted, setIsDeleteGroupPermitted] = useState(false);
    const [pageNo, setPageNo, dataLength, setDataLength, sorting, setSorting] = useDataQueries();
    const listBootstrap = getListCacheBootstrap("groups", { dataLength, sorting });

    const [groups, setGroups] = useState<any>(listBootstrap.items);
    const [isLoading, setIsLoading] = useState(!listBootstrap.hasCache);
    const [dataLoad, setDataLoad] = useState(!listBootstrap.hasCache);
    const [allFetched, setAllFetched] = useState(false);
    const [groupsLeft, setGroupsLeft] = useState(true);
    const [totalCount, setTotalCount] = useState(listBootstrap.totalCount);

    useEffect(() => {
        setIsCreateGroupPermission(isFeaturePermitted("AdminPortalCreateGroup"));
        setIsDeleteGroupPermitted(isFeaturePermitted("AdminPortalDeleteGroup"));
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
        const { cacheKey, cached } = getListCacheBootstrap("groups", { dataLength, sorting });

        setPageNo(1);
        setAllFetched(false);
        setGroupsLeft(true);
        if (cached) {
            setGroups(cached.items);
            setTotalCount(cached.totalCount);
            setDataLoad(false);
            setIsLoading(false);
            if (isListFullyFetched(cached.items.length, cached.totalCount, dataLength as number)) {
                setAllFetched(true);
                setGroupsLeft(false);
            }
        } else {
            setIsLoading(true);
            setDataLoad(true);
        }

        fetchGroups(
            1,
            dataLength,
            sorting,
            (response: any) => {
                const nextGroups = response.data.groups ?? [];
                writeListCache(cacheKey, nextGroups, response.data.total_count);
                setIsLoading(false);
                setDataLoad(false);
                setTotalCount(response.data.total_count);
                if (nextGroups.length > 0) {
                    setGroups(nextGroups);
                    storeInLocalStorage(ALL_GROUPS, nextGroups);
                    if (isListFullyFetched(nextGroups.length, response.data.total_count, dataLength as number)) {
                        setAllFetched(true);
                        setGroupsLeft(false);
                    }
                } else {
                    setAllFetched(true);
                    setGroupsLeft(false);
                }
            },
            (error: any) => {
                console.log(error);
                setIsLoading(false);
                setDataLoad(false);
            }
        );
    }, [dataLength, sorting]);

    window.onscroll = function () {
        if (allFetched || isLoading) {
            return;
        }
        if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 1) {
            setPageNo(pageNo + 1);
            setIsLoading(true);
            if (groupsLeft) {
                fetchGroups(
                    pageNo + 1,
                    dataLength,
                    sorting,
                    (response: any) => {
                        setIsLoading(false);
                        setDataLoad(false);
                        setTotalCount(response.data.total_count);
                        if (response.data.groups.length > 0) {
                            const new_groups = response.data.groups;
                            setGroups(new_groups);
                            storeInLocalStorage(ALL_GROUPS, new_groups);
                        } else {
                            setAllFetched(true);
                            setDataLoad(false);
                            setIsLoading(false);
                            setGroupsLeft(false);
                        }
                    },
                    (error: any) => {
                        console.log(error);
                        setIsLoading(false);
                    }
                );
            }
        }
    };

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead
                    id='group-table'
                    pageNo={pageNo as number}
                    dataLength={dataLength as number}
                    setDataLength={setDataLength as (dataLength: number) => void}
                    title="Groups"
                    button={
                        isCreateGroupPermitted && (
                            <Button asChild>
                                <Link to="/group" id='create-group-btn'>
                                    {BUTTONS.CREATE}
                                </Link>
                            </Button>
                        )
                    }
                    currentEnvo={groups.length}
                    totalEnvo={totalCount}
                />
            </div>
            <div>
                <GroupTable id='group-table' allFetched={allFetched} setGroups={setGroups} isLoading={isLoading} dataLength={dataLength as number} sorting={sorting} setSorting={setSorting as (sorting: any) => void} pageNo={pageNo as number} setPageNo={setPageNo as (pageNo: number) => void} groups={groups} dataLoad={dataLoad} />
            </div>
        </div>
    );
};

export default GroupList;
